import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

// Pure: the org-admin terms gate on/off, from `enterprise-hub-terms-gate-enabled`
// (exposed as `enterpriseHubTermsGateEnabled`). Safe default OFF so nobody is blocked
// while the terms rollout is unfinished; flip the flag on once enforcement is ready.
// Note: when enforcement goes live, revisit whether the LD-unavailable default should
// become fail-closed (gate on) for compliance.
export function mapTermsGateFlag(flags: Record<string, unknown>): boolean {
  return flags.enterpriseHubTermsGateEnabled === true;
}

// Thin hook wrapper (SDK glue — covered by E2E, not unit-mocked).
export function useTermsGateEnabled(): boolean {
  return mapTermsGateFlag(useFlags());
}

// Pure: the invited-user name prompt on/off, from `release-hub-onboarding-name-gate`
// (exposed as `releaseHubOnboardingNameGate`). Safe default OFF; turn it on together with
// the WorkOS sign-up name toggle going off.
export function mapNameGateFlag(flags: Record<string, unknown>): boolean {
  return flags.releaseHubOnboardingNameGate === true;
}

export function useNameGateEnabled(): boolean {
  return mapNameGateFlag(useFlags());
}

// Pure: the Guardian trial auto-start / confirm step on hub home, from
// `release-hub-trial-auto-start` (exposed as `releaseHubTrialAutoStart`). Safe default OFF:
// with it off a stored trial intent is left untouched and the Hub behaves as it did before
// the intent flow existed.
export function mapTrialAutoStartFlag(flags: Record<string, unknown>): boolean {
  return flags.releaseHubTrialAutoStart === true;
}

export function useTrialAutoStartEnabled(): boolean {
  return mapTrialAutoStartFlag(useFlags());
}

// Settled = the provider has handed over its client, which happens when `waitForInitialization`
// resolves OR rejects (the 5 s `timeout` in src/main.tsx turns a stalled connection into a
// rejection), so this flips true even when LaunchDarkly never answers; flags then read their
// in-code defaults until the client catches up.
export function useFlagsSettled(): boolean {
  return useLDClient() !== undefined;
}

// How long a page may hold a loader waiting for flags before it continues with the defaults.
// Shorter than the SDK init timeout so a surface never spins longer than a visitor will wait.
export const FLAG_WAIT_MS = 4_000;

const FLUSH_CAP_MS = 300;

export async function flushWithCap(flush: (() => Promise<unknown>) | undefined, capMs = FLUSH_CAP_MS): Promise<void> {
  if (!flush) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => { timer = setTimeout(resolve, capMs); });
  try {
    await Promise.race([flush().then(() => undefined), cap]);
  } catch {
    /* a failing flush must never block the caller */
  } finally {
    clearTimeout(timer);
  }
}

export function useFlushFlags(): () => Promise<void> {
  const ld = useLDClient();
  return () => flushWithCap(ld ? () => ld.flush() : undefined);
}
